import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import AliyunLogo from './components/AliyunLogo';
import TencentLogo from './components/TencentLogo';
import HuaweiCloudLogo from './components/HuaweiCloudLogo';
import CtyunLogo from './components/CtyunLogo';

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #ffffff;
  color: #1d1d1d;
`;

const Header = styled.header`
  padding: 2rem;
  text-align: center;
  background: #f48120;
  color: white;

  h1, p {
    color: white;
  }
`;

const Section = styled.section`
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  
  &:nth-of-type(even) {
    background: #fff8f2;
  }
`;

const PartnerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const PartnerLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  transition: transform 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-5px);
  }

  svg {
    max-width: 160px;
    height: auto;
    fill: #333333;
  }

  &[href*="aliyun"] svg {
    fill: #FF6A00;
  }

  img {
    max-width: 160px;
    height: auto;
  }
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ServiceCard = styled.a`
  display: block;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  transition: all 0.3s ease;
  text-decoration: none;
  color: inherit;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(244, 129, 32, 0.1);
    
    &:before {
      opacity: 0.05;
    }
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #f48120;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  h3 {
    margin: 0 0 1rem;
    position: relative;
  }
  
  p {
    margin: 0;
    position: relative;
    color: #666;
  }
`;

const ContactInfo = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(244, 129, 32, 0.2);
  font-size: 0.95rem;
  color: #666;

  p {
    margin: 0.5rem 0;
  }

  a {
    color: #f48120;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

function App() {
  return (
    <Container>
      <Header>
        <motion.h1 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          博恩网云科技
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          您的云计算技术服务专家
        </motion.p>
      </Header>

      <Section>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2>关于我们</h2>
          <p>博恩网云是您值得信赖的云计算技术合作伙伴。我们专注于为企业提供全方位的云计算技术支持和运维服务，帮助您实现数字化转型，提升业务效率。</p>
          <ContactInfo>
            <p>联系人：张先生</p>
            <p>邮箱：<a href="mailto:chinanet@live.in">chinanet@live.in</a></p>
            <p>电话：<a href="tel:+8616618891505">(+86) 166 1889 1505</a></p>
          </ContactInfo>
        </motion.div>
      </Section>

      <Section>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2>我们的服务</h2>
          <div className="services-grid">
            <div className="service-item">
              <h3>云架构设计</h3>
              <p>量身定制云基础设施方案，优化资源配置，确保性能与成本的最佳平衡。</p>
            </div>
            <div className="service-item">
              <h3>技术运维支持</h3>
              <p>7*24小时专业运维服务，保障您的系统稳定运行。</p>
            </div>
            <div className="service-item">
              <h3>安全防护</h3>
              <p>全面的云安全解决方案，为您的业务保驾护航。</p>
            </div>
          </div>
        </motion.div>
      </Section>

      <Section>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2>服务体系</h2>
          <ServiceGrid>
            <ServiceCard href="/service/247" target="_blank" rel="noopener noreferrer">
              <h3>全天候高效服务</h3>
              <p>7x24 小时为您提供专业服务</p>
            </ServiceCard>
            <ServiceCard href="/service/tools" target="_blank" rel="noopener noreferrer">
              <h3>多渠道无忧服务</h3>
              <p>多种查询、诊断等服务工具，快速解决常见问题</p>
            </ServiceCard>
            <ServiceCard href="/service/vip" target="_blank" rel="noopener noreferrer">
              <h3>专属贴身服务</h3>
              <p>1v1 极速响应、专属保障的贴身服务</p>
            </ServiceCard>
            <ServiceCard href="/service/cloud" target="_blank" rel="noopener noreferrer">
              <h3>专业上云服务</h3>
              <p>上云全周期的技术咨询与实施服务</p>
            </ServiceCard>
          </ServiceGrid>
        </motion.div>
      </Section>

      <Section>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2>合作伙伴</h2>
          <PartnerGrid>
            <PartnerLink href="https://www.aliyun.com" target="_blank" rel="noopener noreferrer">
              <AliyunLogo />
            </PartnerLink>
            <PartnerLink href="https://cloud.tencent.com" target="_blank" rel="noopener noreferrer">
              <TencentLogo />
            </PartnerLink>
            <PartnerLink href="https://www.huaweicloud.com" target="_blank" rel="noopener noreferrer">
              <HuaweiCloudLogo />
            </PartnerLink>
            <PartnerLink href="https://www.ctyun.cn" target="_blank" rel="noopener noreferrer">
            <CtyunLogo />
            </PartnerLink>
          </PartnerGrid>
        </motion.div>
      </Section>
    </Container>
  );
}

export default App;
